import { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useAuth } from '../lib/AuthContext';
import { useCart } from '../lib/CartContext';
import { useTheme } from '../lib/ThemeContext';
import { supabase } from '../lib/supabaseClient';
import useTranslation from 'next-translate/useTranslation';
import ThemeSwitcher from './ThemeSwitcher';
import {
  FiShoppingCart,
  FiUser,
  FiMenu,
  FiX,
  FiSearch,
  FiLogOut,
  FiPackage,
  FiSettings,
} from 'react-icons/fi';

export default function Header() {
  const { user } = useAuth();
  const { cartProducts } = useCart();
  const { theme } = useTheme();
  const router = useRouter();
  const { t } = useTranslation('common');
  const isHomeRoute = router.pathname === '/';
  const isLegalRoute = router.asPath.startsWith('/sub/');
  const isShopRoute = router.pathname === '/shop';
  const isContactRoute = router.pathname === '/contact';
  const isProductRoute = router.pathname === '/product/[id]';
  const isAdminRoute = router.pathname.startsWith('/admin');
  const isProfileRoute = router.pathname === '/profile' || router.pathname === '/account';
  const isDarkRoute =
    isLegalRoute ||
    isShopRoute ||
    isContactRoute ||
    isProductRoute ||
    isAdminRoute ||
    isProfileRoute ||
    router.pathname === '/404' ||
    router.pathname === '/orders' ||
    router.pathname === '/cart' ||
    router.pathname === '/login' ||
    router.pathname === '/signup' ||
    router.pathname === '/forgot-password' ||
    router.pathname === '/reset-password' ||
    router.pathname === '/reset-password/confirm' ||
    router.pathname === '/verify-email';
  const isDarkTheme = theme === 'dark';
  const normalizePath = (path: string) => {
    const cleanPath = path.split('?')[0].split('#')[0];
    if (cleanPath.length > 1 && cleanPath.endsWith('/')) {
      return cleanPath.slice(0, -1);
    }
    return cleanPath;
  };
  const currentPath = normalizePath(router.asPath || router.pathname || '/');

  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const userMenuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const mobileSearchRef = useRef<HTMLInputElement>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

  const cartCount = cartProducts.reduce((acc, p) => acc + p.quantity, 0);

  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'Shop', href: '/shop' },
    { label: 'Contact', href: '/contact' },
  ];

  const cartHref = '/cart';
  const profileHref = '/profile';
  const ordersHref = '/orders';
  const adminHref = '/admin';
  const loginHref = '/login';
  const headerClassName = isHomeRoute
    ? 'absolute top-0 left-0 right-0 z-50 w-full bg-white/10 dark:bg-black/10 backdrop-blur-xs border-b border-white/15'
    : isDarkRoute
      ? 'sticky top-0 z-50 w-full bg-[#e8e8e8]/95 dark:bg-black/10 backdrop-blur-sm border-b border-[#d6ccb7] dark:border-white/5'
      : 'sticky top-0 z-50 w-full bg-[#f6f1e7]/95 dark:bg-[#e8e8e8]/95 backdrop-blur-md border-b border-[#d6ccb7]';
  const navbarHoverAccentClass = isDarkTheme ? 'hover:text-white' : 'hover:text-primary';

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const fetchRole = async () => {
      if (!user) {
        setIsAdmin(null);
        return;
      }

      try {
        const res = await fetch('/api/me', { credentials: 'include' });
        if (!res.ok) {
          if (!isCancelled) setIsAdmin(false);
          return;
        }
        const data = await res.json();
        if (!isCancelled) {
          setIsAdmin(!!data.isAdmin);
        }
      } catch {
        if (!isCancelled) setIsAdmin(false);
      }
    };

    fetchRole();

    return () => {
      isCancelled = true;
    };
  }, [user]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      router.push(`/shop?q=${encodeURIComponent(searchQuery.trim())}`);
      setMobileOpen(false);
      setMobileSearchOpen(false);
    }
  };

  const toggleMobileSearch = () => {
    setMobileSearchOpen((prev) => {
      const next = !prev;
      if (next) setMobileOpen(false);
      return next;
    });
  };

  const toggleMobileMenu = () => {
    setMobileOpen((prev) => {
      const next = !prev;
      if (next) setMobileSearchOpen(false);
      return next;
    });
  };

  useEffect(() => {
    const q = typeof router.query.q === 'string' ? router.query.q : '';
    if (q) setSearchQuery(q);
  }, [router.query.q]);

  useEffect(() => {
    if (mobileSearchOpen && mobileSearchRef.current) {
      mobileSearchRef.current.focus();
    }
  }, [mobileSearchOpen]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    document.cookie = '__session=; path=/; max-age=0';
    setUserMenuOpen(false);
    router.push(loginHref);
  };

  return (
    <>
      <header
        className={headerClassName}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 h-16 md:h-20 flex items-center justify-between gap-2 md:gap-4">

        {/* Logo */}
        <Link
          href={'/'}
          className="flex-shrink-0 flex items-center"
          aria-label="GadgetFever home page"
        >
          <Image
            src="/GadgetFever/GadgetFever_webp_logo.webp"
            alt="GadgetFever electronics store logo"
            width={168}
            height={72}
            className="h-[40px] md:h-[56px] w-auto object-contain"
            priority
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6">
          {navLinks.map((link) => (
            (() => {
              const normalizedLink = normalizePath(link.href);
              const isActiveLink = currentPath === normalizedLink;
              return (
            <Link
              key={link.href}
              href={link.href}
              className={`text-[15px] font-medium transition-colors ${isDarkTheme ? 'hover:text-white' : 'hover:text-black'} ${
                isActiveLink
                  ? isDarkTheme ? 'text-white font-semibold' : 'text-black font-semibold'
                  : isDarkTheme ? 'text-white/90' : 'text-gray-900'
              }`}
            >
              <span
                className={`inline-block pb-0.5 ${
                  isActiveLink
                    ? isDarkTheme ? 'border-b-2 border-white' : 'border-b-2 border-black'
                    : 'border-b-2 border-transparent'
                }`}
              >
                {link.label}
              </span>
            </Link>
              );
            })()
          ))}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-1 md:gap-2">

          {/* Inline search (desktop) */}
          <form onSubmit={handleSearch} className="hidden md:flex items-center w-[300px] rounded-full border border-[#cbbfa7]/80 bg-transparent backdrop-blur-md px-3 py-1.5">
            <input
              ref={searchRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={'Search'}
              className={`w-full bg-transparent text-sm focus:outline-none ${isDarkTheme ? 'text-[#cbbfa7] placeholder-[#cbbfa7]' : 'text-[#3c2f25] placeholder-black'}`}
            />
            <button
              type="submit"
              className={`ml-2 inline-flex items-center justify-center transition-colors ${isDarkTheme ? 'text-[#cbbfa7] hover:text-[#cbbfa7]/75' : 'text-primary hover:text-primaryDark'}`}
              aria-label="Search"
            >
              <FiSearch className="w-4 h-4" />
            </button>
          </form>

          {/* Mobile search shortcut */}
          <button
            type="button"
            onClick={toggleMobileSearch}
            className={`md:hidden h-9 w-9 inline-flex items-center justify-center rounded-lg transition-colors ${isDarkTheme ? 'text-white hover:text-white hover:bg-white/10' : 'text-black hover:text-black hover:bg-black/10'}`}
            aria-label="Search"
          >
            <FiSearch className="w-5 h-5" />
          </button>

          <ThemeSwitcher />

          {/* Cart */}
          <Link
            href={cartHref}
            className={`relative h-9 w-9 inline-flex items-center justify-center rounded-lg transition-colors ${isDarkTheme ? 'text-white hover:text-white hover:bg-white/10' : 'text-black hover:text-black hover:bg-black/10'}`}
            aria-label="Cart"
          >
            <FiShoppingCart className="w-5 h-5" />
            {cartCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-white text-[10px] font-bold px-1 bg-black">
                {cartCount}
              </span>
            )}
          </Link>

          {/* User menu / Login (desktop order: after language) */}
          {user ? (
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen((v) => !v)}
                className={`h-9 w-9 inline-flex items-center justify-center rounded-lg transition-colors ${isDarkTheme ? 'text-white hover:text-white hover:bg-white/10' : 'text-black hover:text-black hover:bg-black/10'}`}
                aria-label="Account"
              >
                <FiUser className="w-5 h-5" />
              </button>
              {userMenuOpen && (
                <div className={`absolute right-0 mt-2 w-48 rounded-xl shadow-xl overflow-hidden z-50 ${isDarkTheme ? 'bg-black border border-neutral-800' : 'bg-[#e8e8e8] border border-[#d6ccb7]'}`}>
                  <div className={`px-4 py-3 ${isDarkTheme ? 'border-b border-neutral-800' : 'border-b border-[#d6ccb7]'}`}>
                    <p className={`text-xs truncate ${isDarkTheme ? 'text-gray-400' : 'text-gray-500'}`}>{user.email}</p>
                  </div>
                  <Link
                    href={profileHref}
                    onClick={() => setUserMenuOpen(false)}
                    className={`flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${isDarkTheme ? 'text-white hover:bg-white/10' : 'text-gray-900 hover:bg-black/10'}`}
                  >
                    <FiUser className="w-4 h-4" />
                    {'Profile'}
                  </Link>
                  <Link
                    href={ordersHref}
                    onClick={() => setUserMenuOpen(false)}
                    className={`flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${isDarkTheme ? 'text-white hover:bg-white/10' : 'text-gray-900 hover:bg-black/10'}`}
                  >
                    <FiPackage className="w-4 h-4" />
                    {'Orders'}
                  </Link>
                  {isAdmin && (
                    <Link
                      href={adminHref}
                      onClick={() => setUserMenuOpen(false)}
                      className={`flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${isDarkTheme ? 'text-white hover:bg-white/10' : 'text-gray-900 hover:bg-black/10'}`}
                    >
                      <FiSettings className="w-4 h-4" />
                      {'Admin'}
                    </Link>
                  )}
                  <button
                    onClick={handleSignOut}
                    className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-500 transition-colors ${isDarkTheme ? 'hover:bg-red-500/10' : 'hover:bg-red-50'}`}
                  >
                    <FiLogOut className="w-4 h-4" />
                    {'Sign out'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              href={loginHref}
              className={`hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white transition-colors ${isDarkTheme ? 'bg-black hover:bg-black/80' : 'bg-primary hover:bg-primaryDark'}`}
            >
              {'Login'}
            </Link>
          )}

          {/* Mobile menu toggle */}
          <button
            onClick={toggleMobileMenu}
            className={`md:hidden h-9 w-9 inline-flex items-center justify-center rounded-lg transition-colors ${isDarkTheme ? 'text-white hover:text-white hover:bg-white/10' : 'text-black hover:text-black hover:bg-black/10'}`}
            aria-label="Menu"
          >
            {mobileOpen ? <FiX className="w-5 h-5" /> : <FiMenu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      </header>

      {mobileSearchOpen && (
        <div className={`md:hidden fixed inset-x-0 top-16 z-40 px-4 py-3 ${isDarkTheme ? 'bg-black border-t border-neutral-800' : 'bg-[#e8e8e8] border-t border-[#d9d9d9]'}`}>
          <form onSubmit={handleSearch} className={`flex items-center w-full rounded-xl border px-3 py-2 ${isDarkTheme ? 'border-gray-700 bg-black' : 'border-[#cbbfa7] bg-white'}`}>
            <input
              ref={mobileSearchRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products..."
              className={`w-full bg-transparent text-sm focus:outline-none ${isDarkTheme ? 'text-white placeholder-gray-400' : 'text-[#3c2f25] placeholder-gray-500'}`}
            />
            <button
              type="submit"
              className={`ml-2 inline-flex items-center justify-center transition-colors ${isDarkTheme ? 'text-white hover:text-gray-300' : 'text-primary hover:text-primaryDark'}`}
              aria-label="Submit search"
            >
              <FiSearch className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}

      {/* Mobile nav */}
      {mobileOpen && (
        <div className={`md:hidden fixed inset-x-0 bottom-0 top-16 z-40 px-4 py-6 space-y-1 overflow-y-auto ${isDarkTheme ? 'bg-black border-t border-neutral-800' : 'bg-[#e8e8e8] border-t border-[#d9d9d9]'}`}>
          {navLinks.map((link) => {
            const isActive = normalizePath(router.asPath) === normalizePath(link.href);
            return (
            <Link
              key={link.href}
              href={link.href}
              onClick={() => setMobileOpen(false)}
              className={`block px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                isActive
                  ? isDarkTheme ? 'bg-white/10 text-white' : 'bg-black/10 text-black'
                  : isDarkTheme ? 'text-white/90 hover:bg-white/10' : 'text-gray-900 hover:bg-black/10'
              }`}
            >
              {link.label}
            </Link>
            );
          })}
          {!user && (
            <Link
              href={loginHref}
              onClick={() => setMobileOpen(false)}
              className={`block mt-2 px-3 py-2.5 rounded-xl text-sm font-medium text-center text-white transition-colors ${isDarkTheme ? 'bg-white/20 hover:bg-white/30' : 'bg-primary hover:bg-primaryDark'}`}
            >
              {'Login'}
            </Link>
          )}
        </div>
      )}
    </>
  );
}
